/// <reference path="Utilities/_reference.ts" />

module YVR.Itinerary {
	// Dependencies
	import flights = YVR.Utilities.Flights;
	import gtm = YVR.Utilities.Gtm;
	import i18n = YVR.Utilities.I18n;
	import logger = YVR.Utilities.Logger;
	import urls = YVR.Utilities.Urls;

	// Constants
	const ANALYTICS_CATEGORY = 'passenger-guides';
	const AUTOCOMPLETE_MIN_LENGTH = 3;
	const CURRENT_LANGUAGE = i18n.getCurrentLanguage();
	const ERROR_CLASS_NAME = 'yvr-itinerary-error';
	const FLIGHT_SERVICE_URL = `/${CURRENT_LANGUAGE}/_api/Flights`;
	const ITINERARY_SERVICE_URL = `/${CURRENT_LANGUAGE}/_api/Itinerary`;

	// Globals
	const classNames = {
		buttonFlightSelected: 'yvr-itinerary-cards__select-flight--selected',
		collectionSelected: 'yvr-itinerary-cards__collection--has-selection',
		flightSelected: 'yvr-itinerary-cards__result--selected'
	};

	const elements = {
		cards: $('.yvr-itinerary-cards'),
		errors: $('.yvr-itinerary-errors'),
		form: $('.yvr-itinerary-selector'),
		inputs: $('.yvr-itinerary-selector__input'),
		map: $('.yvr-itinerary-map'),
		showGuideButton: $('.yvr-itinerary-selector__show-guide'),
		steps: $('.yvr-itinerary-steps')
	};

	const templates = {
		cards: $('#yvr-flights-template'),
		steps: $('#yvr-itinerary-steps-template')
	};

	let flightData: IFlight[];

	// @TODO: Move these to data attributes on the input fields.
	let savedFlights = {};


	/**
	 * Initialise the itinerary component.
	 */
	function main(): void {
		// Check to see if the component is on the page.
		if (elements.cards.length) {
			initGetFlightNumbers()
				.then(() => {
					initFlightValidator();
					initFlightAutocomplete(elements.inputs);
					eventBindings();

					initUrlParameters();
				});
		}
	}


	/**
	 * Check the query string parameters to see if there is an initial value.
	 */
	function initUrlParameters(): void {
		const flightNumber = urls.getQueryStringParameter('flight-number');

		if (flightNumber) {
			elements.showGuideButton.trigger('click');
		}
	}


	/**
	 * User event bindings for the itinerary component.
	 */
	function eventBindings(): void {
		elements.form.on('click', '.yvr-itinerary-selector__show-guide', onShowGuideClick);
		elements.cards.on('click', 'button.yvr-itinerary-cards__select-flight', onSelectFlightButtonClick);
	}


	//#region Errors

	/**
	 * Clear all error messages.
	 */
	function clearErrors(className = ERROR_CLASS_NAME): void {
		$(`.${className}`).remove();
	}

	/**
	 * Add an error message to the page.
	 * @param message
	 */
	function addError(message = '', className = ERROR_CLASS_NAME): void {
		clearErrors();

		if (message === '') {
			message = elements.form.data('form-error');
		}

		const html = `<p class="${className}">${message}</p>`;
		elements.steps.after(html);
	}

	//#endregion


	//#region Flight Numbers

	/**
	 * Get a list of flight numbers from the service.
	 */
	function initGetFlightNumbers(): JQueryPromise<any[]> {
		return $.get({
			url: FLIGHT_SERVICE_URL,
			data: {
				$select: [
					'FlightID',
					'FlightNumber',
					'FlightType'
				].join(','),
				$orderby: 'FlightNumber'
			}
		})
		.then((data: IFlightCollection) => {
			flightData = data.value.filter((value: IFlight, index: number, array: IFlight[]) => {
				// Remove the duplicates
				return !(array[index - 1] && array[index - 1].FlightNumber === array[index].FlightNumber && array[index - 1].FlightType === array[index].FlightType);
			});

			return flightData;
		})
		.fail((xhr: JQueryXHR, textStatus: string) => {
			logger.error('[itinerary][initGetFlightNumbers] An error occured when retrieving the overall flight number list.');
			addError();
		});
	}

	//#endregion


	//#region Flight Inputs


	/**
	 * Validate whether or not the input value matches a departing flight.
	 * @returns True if the flight is a departing flight.
	 */
	function validateConnectingFlightType(value: string): boolean {
		return value &&
			value.length &&
			flightData.filter((flight) => flight.FlightNumber === value && flight.FlightType === 'D').length > 0;
	}


	/**
	 * Validate whether or not the input value is a valid flight number.
	 * @param value The flight number input.
	 * @returns True if the flight number is valid.
	 */
	function validateFlightNumber(value: string): boolean {
		return value &&
			value.length &&
			flightData.filter((flight) => flight.FlightNumber.toUpperCase() === value.toUpperCase().replace(/\s/, '')).length > 0;
	}


	/**
	 * Return an array of unique values.
	 * @param value
	 * @param index
	 * @param self
	 */
	function unique(value: string, index: number, self: string[]) {
		return self.indexOf(value) === index;
	}


	/**
	 * Validate whether or not the input values have different flight types.
	 * @returns True if the flight types are different.
	 */
	function validateFlightType(value: string): boolean {
		const numbers = getInputValues(elements.inputs);
		let flight0 = flightData.filter((flight) => flight.FlightNumber === numbers[0]).map((flight) => flight.FlightType).filter(unique);
		let flight1 = flightData.filter((flight) => flight.FlightNumber === numbers[1]).map((flight) => flight.FlightType).filter(unique);

		if (flight0.length && flight1.length) {
			// If the first flight can arrive, the second flight must have a departure
			return flight0.indexOf('A') > -1 && flight1.indexOf('D') > -1;
		}

		return true;
	}


	/**
	 * Initalise the custom validators for input fields.
	 */
	function initFlightValidator(): void {
		if ((window as any).Parsley) {
			// Validate whether or not the input values have different flight types.
			(window as any).Parsley.addValidator('connectingflighttypeinvalid', {
				requirementType: 'string',
				validateString: validateConnectingFlightType
			});

			// Validate whether or not the input value is a valid flight number.
			(window as any).Parsley.addValidator('flightnumberinvalid', {
				requirementType: 'string',
				validateString: validateFlightNumber
			});

			// Validate whether or not the input values have different flight types.
			(window as any).Parsley.addValidator('flighttypeinvalid', {
				requirementType: 'string',
				validateString: validateFlightType
			});
		}
	}


	/**
	 * Set an autocomplete on the form inputs to show all flight numbers, removing duplicates.
	 * @param inputs The flight number inputs.
	 */
	function initFlightAutocomplete(inputs: JQuery): void {
		inputs.each((inputIndex, element) => {
			const input = $(element);
			let previousInput: string;

			input.autocomplete({
				lookup: (query: string, done: Function) => {
					const flightNumber = transformInputValue(query);

					// Double-check minimum length since the transform can remove characters.
					// This does not prevent autocomplete from breaking on longer values.
					if (flightNumber.length >= AUTOCOMPLETE_MIN_LENGTH) {
						let suggestions: AutocompleteSuggestion[];
						const flightType = input.data('flight-type');

						suggestions = flightData.filter((flight: IFlight) => {
								let suggest = true;

								// If the input is only for a specific flight type, filter the rest out.
								if (flightType && flightType.length) {
									suggest = flight.FlightType === flightType;
								}

								// Filter out flight numbers that do not match.
								return suggest && flight.FlightNumber.indexOf(flightNumber) > -1;
							})
							.filter((flight: IFlight, index: number, array: IFlight[]) => {
								// Remove the duplicates
								return !(array[index - 1] && array[index - 1].FlightNumber === array[index].FlightNumber);
							})
							.map((flight) => {
								// Re-map the flight data to the autocomplete suggestion format.
								return {
									data: flight,
									value: flight.FlightNumber
								} as AutocompleteSuggestion;
							});

						done({ suggestions });
					}
				},
				minChars: AUTOCOMPLETE_MIN_LENGTH,
				onInvalidateSelection() {
					logger.log(`[itinerary][onInvalidateSelection] User has made an invalid flight number selection.`);
				},
				onSelect(suggestion: AutocompleteSuggestion) {
					// Check if the input is exactly the same as before.
					if (previousInput !== suggestion.value) {
						previousInput = suggestion.value;
						elements.form.parsley().validate();
					}
				}
			});
		});
	}


	/**
	 * Transform user input values to match flight numbers.
	 * @param value A flight number input.
	 */
	function transformInputValue(value: string): string {
		return value.replace(/\s/, '')
			.toUpperCase();
	}


	/**
	 * Get a flight input value.
	 * @param input A flight number input.
	 */
	function getInputValue(input: HTMLInputElement): string {
		return transformInputValue(input.value);
	}


	/**
	 * Get an array of flight input values.
	 * @param inputs The flight number inputs as a JQuery collection.
	 * @return An array of flight input values.
	 */
	function getInputValues(inputs: JQuery): string[] {
		return inputs.toArray()
			.map(getInputValue)
			.filter((value: string) => value && value.length);
	}

	//#endregion


	//#region Flight Cards

	/**
	 * Show the initial flight cards when a user actions on the show guide button.
	 * @param e The event object from the show guide button.
	 */
	function onShowGuideClick(e: JQueryEventObject): void {
		e.preventDefault();

		// Remove any errors, since clicking on show guide is a new action.
		clearErrors();

		// Reset current flight IDs.
		savedFlights = {};

		// Get the user inputs, necessary for both success and fail case.
		const numbers = getInputValues(elements.inputs);

		// If the form validates
		if (elements.form.parsley().validate()) {
			getFlightCard(numbers);

			// If this is a connecting flight, there should be multiple flight numbers
			if (numbers.length > 1) {
				// Analytics value is delimited by pipes
				const value = numbers.join('|');
				gtm.eventToDataLayer(ANALYTICS_CATEGORY, 'connecting-flight', value);
			}
		} else {
			// If the form didn't validate, but we still have user inputs
			if (numbers.length) {
				// Loop through each of the inputs
				for (let i = 0; i < numbers.length; i++) {
					// Check if the input is valid or not
					if (numbers[i] && !validateFlightNumber(numbers[i])) {
						// If invalid, send analytic event with the field number (1|2) and the user input
						gtm.eventToDataLayer(ANALYTICS_CATEGORY, `flight-not-found|field-${i + 1}`, numbers[0]);
					}
				}
			}
		}
	}

	/**
	 * Get the flight cards using the inputted flight numbers.
	 * @param numbers
	 */
	function getFlightCard(numbers: string[]): void {
		if (numbers.length) {
			flights.getFlightByFlightNumber(numbers)
				.then(transformFlightDetails)
				.then(renderFlightDetails)
				.fail((xhr: JQueryXHR, textStatus: string) => {
					logger.error('[itinerary][getFlightCard] An error occured retrieving the flight data. ${textStatus}');
					addError();
				});
		}
	}


	/**
	 * Transform the flight details.
	 * @param data
	 */
	function transformFlightDetails(data: any): Object {
		if (data && data.value && data.value.length) {
			const collections = {};

			// Loop through all the results and group them by flight number
			data.value.forEach((flight) => {
				const key = flight.FlightType;

				if (collections[key] === undefined) {
					collections[key] = [];
				}

				collections[key].push(flight);
			});

			// If there are more than one collection but only one unique flight number, we cannot auto-select.
			// This is for special case flights (e.g. CX888 and CX889) where they have both arriving and departing flights.
			if (getInputValues(elements.inputs).length !== 1 || Object.keys(collections).length <= 1) {
				// If a flight only has one result, we don't need a user to select it later.
				// Save the flight number to our global variable.
				for (let key in collections) {
					if (collections.hasOwnProperty(key)) {
						const collection = collections[key];

						if (collection.length === 1) {
							const flight = collection[0];

							if (flight.FlightNumber) {
								collection.Selected = true;
								flight.Selected = true;
								savedFlights[key] = flight;
								logger.log(`[itinerary][transformFlightDetails] Flight collection ${key} has been set with ${flight.FlightID}.`);
							}
						}
					}
				}
			}

			initItinerarySteps();

			return collections;
		}

		return null;
	}


	/**
	 * Render the flight detail data from the gateway wrapper.
	 * @param {obj} data The data returned from the gateway wrapper.
	 */
	function renderFlightDetails(data: Object): void {
		if (data) {
			const source = templates.cards.html();
			const template = Handlebars.compile(source);
			const context = {
				connecting: false,
				flights: data
			};

			if (context.flights && Object.keys(context.flights).length > 1 && getInputValues(elements.inputs).length > 1) {
				context.connecting = true;
			}

			const html = template(context);
			elements.cards.html(html).show();

			clearItinerarySteps();
		}
	}

	//#endregion


	//#region Flight Itinerary

	/**
	 * Select the current flight of the multiple flights.
	 * @param e
	 */
	function onSelectFlightButtonClick(e: JQueryEventObject): void {
		e.preventDefault();
		clearErrors();

		const button = $(e.target);

		logger.log(`[itinerary][onSelectFlightButtonClick] Clicked on select flight button.`);

		// Get current flight id
		const collectionId = button.closest('.yvr-itinerary-cards__collection').data('flight-collection');

		// Set the flight number
		const flight = button.data('flight');

		// If the flight is already selected
		if (savedFlights[collectionId] === flight) {
			// Remove selected button state
			button.removeClass(classNames.buttonFlightSelected);
			button.closest(`.${classNames.collectionSelected}`).removeClass(classNames.collectionSelected);

			savedFlights[collectionId] = null;
		} else {
			if (collectionId && !isNaN(flight.FlightID)) {
				savedFlights[collectionId] = flight;

				logger.log(`[itinerary][onSelectFlightButtonClick] Flight collection ${collectionId} has been set with ${flight.FlightID}.`);

				// Add selected button icon, remove them from other buttons
				button.closest('.yvr-itinerary-cards__result').addClass(classNames.flightSelected)
					.closest('.yvr-itinerary-cards__collection').addClass(classNames.collectionSelected)
					.find(`.${classNames.buttonFlightSelected}`).removeClass(classNames.buttonFlightSelected);

				button.addClass(classNames.buttonFlightSelected);
			}
		}

		initItinerarySteps();
	}


	/**
	 * Clear the itinerary map.
	 */
	function clearItinerarySteps(): void {
		elements.steps.empty();
		elements.map.empty();
	}


	/**
	 * Check to see if the itinerary steps can start.
	 */
	function initItinerarySteps() {
		const ids: number[] = [];

		clearItinerarySteps();

		if (savedFlights['A'] && savedFlights['A'].FlightID) {
			ids.push(savedFlights['A'].FlightID);
		}

		if (savedFlights['D'] && savedFlights['D'].FlightID) {
			ids.push(savedFlights['D'].FlightID);
		}

		const inputs = getInputValues(elements.inputs);

		// Check to see if the number of flights selected is one or two.
		if (ids.length >= inputs.length) {
			// Run the flight itinerary service with the necessary flight numbers
			getItinerarySteps(ids)
				.then(transformItinerarySteps)
				.then(renderItinerarySteps)
				.then(YVR.ItineraryMap.runMap)
				.fail((xhr: JQueryXHR, textStatus: string) => {
					logger.error(`[itinerary][initItinerarySteps] An error occured when retrieving the steps from the itinerary service. ${textStatus}`);
					gtm.eventToDataLayer(ANALYTICS_CATEGORY, 'service-error', textStatus);

					addError();
				});
		}
	}


	/**
	 * Returns the itinerary steps from the itinerary service.
	 * @param ids
	 */
	function getItinerarySteps(ids: number[]): JQueryPromise<IItinerary> {
		return $.get({
			url: `${ITINERARY_SERVICE_URL}/${ids.join('/')}`
		});
	}


	/**
	 * Transform the itinerary step data.
	 * @param data The original itinerary data from the itinerary service.
	 * @return The transformed itinerary data.
	 */
	function transformItinerarySteps(data: IItinerary) {
		data.FlightIds = {};

		if (savedFlights['A']) {
			data.FlightIds['A'] = savedFlights['A'].FlightNumber;
		}

		if (savedFlights['D']) {
			data.FlightIds['D'] = savedFlights['D'].FlightNumber;
		}

		return data;
	}


	/**
	 * Render the itinerary steps data.
	 * @param {obj} data The transformed itinerary data.
	 * @return Return the itinerary data for chaining.
	 */
	function renderItinerarySteps(data: IItinerary): IItinerary {
		const source = templates.steps.html();
		const template = Handlebars.compile(source);
		const html = template(data);
		$('.yvr-itinerary-steps').html(html);

		return data;
	}


	//#endregion

	main();
}
